import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { aiqApi } from '@/api/aiq'
import { learnerApi } from '@/api/b2b'
import {
  Brain,
  TrendingUp,
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'

export default function AiqResult() {
  const { t } = useTranslation('learner')
  const { classProductId, type } = useParams<{ classProductId: string; type: string }>()
  const navigate = useNavigate()

  const isPreTest = type === 'pre'

  // Get course info to retrieve productId (same format as MyCourses.tsx for cache sharing)
  const { data: coursesData, isLoading: isLoadingCourse } = useQuery({
    queryKey: ['myClassCourses'],
    queryFn: () => learnerApi.getMyClassCourses(),
    staleTime: 5 * 60 * 1000, // 5분간 캐시 유지 (페이지 이동 시 재요청 방지)
    gcTime: 10 * 60 * 1000, // 10분간 가비지 컬렉션 방지
  })

  const courses = coursesData?.data?.data || []
  const currentCourse = courses.find(
    (c) => c.classProductId === Number(classProductId)
  )
  const productId = currentCourse?.productId

  // Fetch AIQ status to get result data
  const { data: aiqStatus, isLoading: isLoadingStatus, error } = useQuery({
    queryKey: ['aiqStatus', productId, classProductId],
    queryFn: async () => {
      if (!productId) return null
      const response = await aiqApi.getStatus(productId, Number(classProductId))
      return response.data.data
    },
    enabled: !!productId,
  })

  const handleBack = () => {
    navigate('/my/aiq')
  }

  // Loading state
  if (isLoadingCourse || isLoadingStatus) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('aiqTest.result.loadingResult')}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !aiqStatus) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('aiqTest.result.loadFailed')}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            {t('aiqTest.goBack')}
          </button>
        </div>
      </div>
    )
  }

  const testData = isPreTest ? aiqStatus.preAiq : aiqStatus.postAiq
  const completedAt = testData.completedAt
  const isCompleted = testData.status === 'completed'

  const colorScheme = isPreTest
    ? { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', light: 'bg-blue-100' }
    : { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', light: 'bg-purple-100' }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('aiqTest.goToAiqBack')}
        </button>

        {/* Main Result Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className={`${isPreTest ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-purple-500 to-purple-600'} p-8 text-white text-center`}>
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              {isPreTest ? (
                <TrendingUp className="w-8 h-8" />
              ) : (
                <Brain className="w-8 h-8" />
              )}
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {isPreTest ? 'Pre-AIQ' : 'Post-AIQ'} {t('aiqTest.result.testResult')}
            </h1>
            <p className="text-white/80">
              {currentCourse?.productName || t('aiqTest.result.courseFallback')}
            </p>
          </div>

          {/* Status Section */}
          <div className="p-8">
            <div className={`${colorScheme.bg} rounded-xl p-6 mb-6`}>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2">
                  {isCompleted ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-4 h-4" />
                      {t('aiqTest.result.completed')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      {t('aiqTest.result.notCompleted')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Completion Date */}
            {completedAt && (
              <p className="text-sm text-gray-500 text-center mb-6">
                {t('aiqTest.result.completedDate')} {new Date(completedAt).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleBack}
                className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t('aiqTest.goToAiq')}
              </button>
              {isPreTest && isCompleted && (
                <button
                  onClick={() => navigate(`/learn/${classProductId}`)}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('aiqTest.startLearning')}
                </button>
              )}
              {!isPreTest && (
                <button
                  onClick={() => navigate('/my/aiq')}
                  className="flex-1 py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {t('aiqTest.result.viewReport')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600 text-center">
            {isPreTest
              ? t('aiqTest.result.preDescription')
              : t('aiqTest.result.postDescription')}
          </p>
        </div>
      </div>
    </div>
  )
}
